import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import AutocompleteField from "../../shared/AutocompleteField/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import "../../styles/scss/Form.scss";

export default function LedgerCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      ledgerName: "",
      ledgerCode: "",
      ledgerType: "",
      natureId: "",
      status: "Active",
      isEditable:1,
    },
    validationSchema: Yup.object({
      ledgerName: Yup.string().required("Ledger name is required"),
      ledgerCode: Yup.string().optional(),
      ledgerType: Yup.string().required("Ledger type is required"),
      natureId: Yup.string().optional(),
    }),
    onSubmit: async (values) => {
      try {
        let response;
        if (id) {
          response = await request.put("/api/updateLedger/" + id, values);
        } else {
          response = await request.post("/api/createLedger", values);
        }

        if (response?.data?.status) {
          toast.success("Ledger saved successfully!");
          navigate("/admin/ledgers");
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            "An error occurred. Please try again.",
        );
      }
    },
  });

  useEffect(() => {
    if (id) {
      const fetchLedger = async () => {
        setLoading(true);
        try {
          const response = await request.get(`/api/getLedgerById/${id}`);
          const ledger = response.data.data;
          formik.setValues({
            ledgerName: ledger.ledgerName || "",
            ledgerCode: ledger.ledgerCode || "",
            ledgerType: ledger.ledgerType || "",
            natureId: ledger.natureId || "",
            status: ledger.status === "Active" || ledger.status === true ? "Active" : "Inactive",
            isEditable: 1,
          });
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      };

      fetchLedger();
    }
  }, [id]);

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={
                id && id !== "new" ? "Edit Ledger" : "Create New Ledger"
              }
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/ledgers"),
              }}
              primaryButton={{
                label: id && id !== "new" ? "Update Ledger" : "Create Ledger",
                icon: <span className="material-symbols-outlined">save</span>,
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <TextField
                    name="ledgerName"
                    label="Ledger Name"
                    type="text"
                    formik={formik}
                    autoFocus
                  />
                </Box>
                <Box className="form-field">
                  <TextField
                    name="ledgerCode"
                    label="Ledger Code"
                    type="text"
                    formik={formik}
                  />
                </Box>
              </Box>
              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <SelectField
                    name="ledgerType"
                    label="Ledger Type"
                    formik={formik}
                    options={[
                      { value: "Expense", label: "Expense" },
                      { value: "Income", label: "Income" },
                      { value: "Any", label: "Any" },
                    ]}
                  />
                </Box>
                <Box className="form-field">
                  <AutocompleteField
                    name="natureId"
                    label="Select Nature"
                    formik={formik}
                    apiUrl="/api/getNatureList"
                    optionLabelKey="natureName"
                    optionValueKey="id"
                  />
                </Box>
              </Box>
              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <SelectField
                    name="status"
                    label="Status"
                    formik={formik}
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}
